import { MenuItem } from '@mui/material';
import brandPixcua from 'assets/images/pixcua.png';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { iComprobante } from 'actions/Comprobante/comprobanteTypes';
import { useEffect, useRef, useState } from 'react';
import {
	iMiEmpresaInfoToPDF,
	initMiEmpresaFormulario,
} from 'actions/MiCuenta/miCuentaTypes';
import {
	getComprobantesById,
	getDocRelacionadoByComprobanteId,
} from 'actions/Comprobante/comprobante';
import { getEmpresaInfoById, getRegimenes } from 'actions/MiCuenta/miCuenta';
import { Toast } from 'Config/swalAlert';
import moment from 'moment';
import MDTypography from 'components/MDTypography';
import QRCode from 'qrcode';
import { getBase64ImageFromURL } from 'helpers/urlTo64';
import { faEnvelope, faLocationDot, faPhone } from '@fortawesome/free-solid-svg-icons';
import layoutCfg from './pdfLayoutConfig.json';
import { log } from 'console';
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const baseURL = process.env.REACT_APP_API_URL;

const fontAwesomeSvg = (iconDefinition: any, color: string) => {
	const [width, height, , , svgPathData] = iconDefinition.icon;
	const paths = (Array.isArray(svgPathData) ? svgPathData : [svgPathData])
		.map((path) => `<path fill="${color}" d="${path}"/>`)
		.join('');
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">${paths}</svg>`;
};

// Config A: estilos y medidas específicas (QR, timbre, totales)
type PDFLayout = {
  variant?: 'pixcuaInvoice';
  styles: { satFontSize: number; bodyFontSize: number; headerFontSize: number };
  qr: { width: number; margin: [number, number, number, number] };
  timbre: { widths: (string | number)[] };
  totales: { widths: (string | number)[] };
  // Nuevo: margen de página configurable para definir desde dónde se pintan los datos
  pageMargins?: [number, number, number, number];
  // Nuevo: alto reservado previo a tabla/totales para cuadrar layout
  contentSpacerHeight?: number;
  backgroundImage?: boolean;
  backgroundUrl?: string;
  background?: {
    width?: number;
    height?: number;
    x?: number;
    y?: number;
  };
  footer?: {
    logoWidth?: number;
    opacity?: number;
    margin?: [number, number, number, number];
  };
  header?: {
    colored?: boolean;
    height?: number;
    logoWidth?: number;
    logoHeight?: number;
    contactIconSize?: number;
    backgroundColor?: string;
    textColor?: string;
    folioBackgroundColor?: string;
    dateBackgroundColor?: string;
    dateWidth?: number;
    dateBlockMargin?: [number, number, number, number];
    datePosition?: 'right' | 'belowLogo';
    usePixcuaPlaceholderLogo?: boolean;
    issuerBesideBackgroundImage?: boolean;
    issuerPosition?: {
      x: number;
      y: number;
      width: number;
    };
    title?: string;
    titleColor?: string;
    titleFontSize?: number;
  };
  theme?: {
    primaryColor?: string;
    totalPriceColor?: string;
    tableHeaderBackgroundColor?: string;
    tableHeaderTextColor?: string;
    alternateRowColor?: string;
    bodyTextColor?: string;
    borderColor?: string;
  };
  tableStyle?: {
    stripedRows?: boolean;
    borderWidth?: number;
    quantityDecimals?: number;
  };
};

const defaultLayout: PDFLayout = {
  styles: { satFontSize: 6, bodyFontSize: 9, headerFontSize: 10 },
  qr: { width: 90, margin: [-7, 10, 0, 0] },
  timbre: { widths: [82, 430] },
  totales: { widths: [65, '*', '*'] },
};

async function loadPDFLayout(empresaId?: number): Promise<PDFLayout> {
  const id = empresaId ?? 0;
  const basePath = '/pdf-layouts';
  try {
    const res = await fetch(`${basePath}/${id}.json`, { cache: 'no-store' });
    if (res.ok) return await res.json();
  } catch (_) {}
  try {
    const res = await fetch(`${basePath}/default.json`, { cache: 'no-store' });
    if (res.ok) return await res.json();
  } catch (_) {}
  return defaultLayout;
}

// Config B: márgenes, tablas y etiquetas desde JSON empaquetado
type LayoutConfig = {
  margins: { header: [number, number, number, number]; datosEmpresa: [number, number, number, number]; footer: [number, number, number, number] };
  table: { widthsWithId: (number | '*')[]; widthsWithoutId: (number | '*')[] };
  styles: { tableHeaderFontSize: number; rowSmallFontSize: number; rowMediumFontSize: number; folioFontSize: number; footerLabelFontSize: number; footerTextFontSize: number; footerAmountFontSize: number };
  labels: { cantidad: string; numeroIdentificacion: string; unidad: string; concepto: string; precio: string; importe: string };
};

function getLayoutConfig(empresaId?: number): LayoutConfig {
  const d = (layoutCfg as any).default || {};
  const per = (layoutCfg as any).empresas?.[String(empresaId ?? '')] || {};
  return {
    margins: { ...(d.margins || {}), ...(per.margins || {}) },
    table: { ...(d.table || {}), ...(per.table || {}) },
    styles: { ...(d.styles || {}), ...(per.styles || {}) },
    labels: { ...(d.labels || {}), ...(per.labels || {}) },
  } as LayoutConfig;
}

//Ver PDF
const openPDFTemplate = async ({
	comprobante,
	empresaInfo,
	regimenCliente,
	regimenEmpresa,
	tipo,
	promesa,
	promesaTemplete,
	docRelacionados,
	onPdfBlobReady,
}: any) => {
  const layout = await loadPDFLayout(comprobante?.empresa_id);
  const cfg = getLayoutConfig(comprobante?.empresa_id);
	const usesColoredHeader = Boolean(layout.header?.colored);
	const isPixcuaInvoice = layout.variant === 'pixcuaInvoice';
	const alternateRowFill = (index: number) =>
		layout.tableStyle?.stripedRows && index % 2 === 1
			? layout.theme?.alternateRowColor
			: undefined;
	const aMoneda = (numero: number, opciones: any) => {
		// Valores por defecto
		opciones = opciones || {};
		opciones.simbolo = opciones.simbolo || '$';
		opciones.separadorDecimal = opciones.separadorDecimal || '.';
		opciones.separadorMiles = opciones.separadorMiles || ',';
		opciones.numeroDeDecimales =
			opciones.numeroDeDecimales >= 0 ? opciones.numeroDeDecimales : 2;
		opciones.posicionSimbolo = opciones.posicionSimbolo || 'i';
		const CIFRAS_MILES = 3;

		// Redondear y convertir a cadena
		let numeroComoCadena = numero.toFixed(opciones.numeroDeDecimales);

		// Comenzar desde la izquierda del separador o desde el final de la cadena si no se proporciona
		let posicionDelSeparador = numeroComoCadena.indexOf(
			opciones.separadorDecimal
		);
		if (posicionDelSeparador === -1)
			posicionDelSeparador = numeroComoCadena.length;
		let formateadoSinDecimales = '',
			indice = posicionDelSeparador;
		// Ir cortando desde la derecha de 3 en 3, y concatenar en una nueva cadena
		while (indice >= 0) {
			let limiteInferior = indice - CIFRAS_MILES;
			// Agregar separador si cortamos más de 3
			formateadoSinDecimales =
				(limiteInferior > 0 ? opciones.separadorMiles : '') +
				numeroComoCadena.substring(limiteInferior, indice) +
				formateadoSinDecimales;
			indice -= CIFRAS_MILES;
		}
		let formateadoSinSimbolo = `${formateadoSinDecimales}${numeroComoCadena.substr(
			posicionDelSeparador,
			opciones.numeroDeDecimales + 1
		)}`;
		return opciones.posicionSimbolo === 'i'
			? opciones.simbolo + formateadoSinSimbolo
			: formateadoSinSimbolo + opciones.simbolo;
	};
	// Probar
	const opcionesPesosMexicanos = {
		numeroDeDecimales: 2,
		separadorDecimal: '.',
		separadorMiles: ',',
		simbolo: '$ ', // Con un espacio, ya que la función no agrega espacios
		posicionSimbolo: 'i', // i = izquierda, d = derecha
	};

	const generateQR = async (text: string) => {
		try {
			const respuesta = await QRCode.toDataURL(text);
			//Validar si text es uan url o si es base 64
			//Si es url transformar
			//Si no solo agregar => data:image/jpeg;base64, al inicio

			return respuesta;
		} catch (err: any) {
			if (
				err.message === 'The amount of data is too big to be stored in a QR Code'
			) {
				return `data:image/jpeg;base64,${comprobante.codigo_qr}`;
			} else {
				return 'data:image/png;base64,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';
			}
		}
	};
	const variable = await generateQR(comprobante.codigo_qr);

	const fechaComprobante = moment(comprobante.fecha).format(
		'DD/MM/YYYY, h:mm:ss a'
	);
	const timbre = () => {
		if (comprobante.timbrado) {
			let codigoQR;
			if (comprobante.codigo_qr === '' || comprobante.codigo_qr == null) {
				codigoQR = null;
			} else {
				codigoQR = {
					image: `${variable}`,
					width: layout.qr.width,
					margin: layout.qr.margin,
				};
			}
			return {
				style: 'tableExample',
				table: {
					widths: layout.timbre.widths,
					body: [
						[
							codigoQR,
							{
								text: [
									{
										text: `Fecha y Hora de Certificacion: ${comprobante.fecha_timbrado}      Certificado SAT: ${comprobante.no_certificado_sat}\n`,
										fontSize: layout.styles.satFontSize,
									},
									{
										text: `Certificado del Emisor:: ${comprobante.no_certificado}           Folio Fiscal: ${comprobante.uuid}\n`,
										fontSize: layout.styles.satFontSize,
									},
									{
										text: `Cadena Original del Complemento de Certificación Digital del SAT: \n`,
										fontSize: layout.styles.satFontSize,
									},
									{
										text: `${comprobante.cadena_original} \n`,
										fontSize: layout.styles.satFontSize,
									},
									{
										text: [
											{
												text: `Sello Digital SAT: `,
												fontSize: layout.styles.satFontSize,
												bold: true,
											},
											{
												text: ` ${comprobante.sello_sat} \n`,
												fontSize: layout.styles.satFontSize,
											},
										],
									},
									{
										text: [
											{
												text: `Sello Digital del Emisor:`,
												fontSize: layout.styles.satFontSize,
												bold: true,
											},
											{
												text: ` ${comprobante.sello} `,
												fontSize: layout.styles.satFontSize,
											},
										],
									},
								],
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else {
			return '';
		}
	};
	const totales = () => {
		if (isPixcuaInvoice) {
			const rows: any[] = [
				['Subtotal', aMoneda(comprobante.subtotal, opcionesPesosMexicanos)],
			];
			if (Number(comprobante.descuento)) rows.push(['Descuento', aMoneda(comprobante.descuento, opcionesPesosMexicanos)]);
			if (Number(comprobante.iva)) rows.push(['I.V.A.', aMoneda(comprobante.iva, opcionesPesosMexicanos)]);
			if (Number(comprobante.retencion_iva)) rows.push(['Retención I.V.A.', aMoneda(comprobante.retencion_iva, opcionesPesosMexicanos)]);
			if (Number(comprobante.retencion_isr)) rows.push(['Retención I.S.R.', aMoneda(comprobante.retencion_isr, opcionesPesosMexicanos)]);
			rows.push([
				{ text: 'TOTAL', bold: true, color: '#FFFFFF' },
				{ text: aMoneda(comprobante.total, opcionesPesosMexicanos), bold: true, color: '#FFFFFF', alignment: 'right' },
			]);

			return {
				table: {
					widths: [82, 88],
					body: rows.map((row, index) => {
						const isTotal = index === rows.length - 1;
						return row.map((cell: any, cellIndex: number) => ({
							...(typeof cell === 'string' ? { text: cell } : cell),
							alignment: cellIndex === 1 ? 'right' : 'left',
							fontSize: isTotal ? 11 : 9,
							bold: isTotal || cellIndex === 0,
							color: isTotal ? '#FFFFFF' : '#174C61',
							fillColor: isTotal ? '#00ABC2' : undefined,
							margin: isTotal ? [7, 7, 7, 7] : [7, 3, 7, 3],
						}));
					}),
				},
				layout: {
					hLineWidth: () => 0,
					vLineWidth: () => 0,
					paddingLeft: () => 0,
					paddingRight: () => 0,
					paddingTop: () => 0,
					paddingBottom: () => 0,
				},
			};
		}
		if (
			!comprobante.iva &&
			!comprobante.retencion_isr &&
			!comprobante.retencion_iva &&
			!comprobante.descuento
		) {
			return {
				table: {
					headerRows: 1,
					widths: layout.totales.widths,
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (
			!comprobante.iva &&
			!comprobante.retencion_isr &&
			!comprobante.retencion_iva
		) {
			return {
				table: {
					headerRows: 1,
					widths: layout.totales.widths,
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Descuento:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.descuento, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (
			!comprobante.descuento &&
			!comprobante.retencion_isr &&
			!comprobante.retencion_iva
		) {
			return {
				table: {
					headerRows: 1,
					widths: [65, '*', '*'],
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (!comprobante.retencion_isr && !comprobante.retencion_iva) {
			return {
				table: {
					headerRows: 1,
					widths: [65, '*', '*'],
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Descuento:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.descuento, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (!comprobante.retencion_isr) {
			return {
				table: {
					headerRows: 1,
					widths: [65, '*', '*'],
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Descuento:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.descuento, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Retención I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.retencion_iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (!comprobante.retencion_iva) {
			return {
				table: {
					headerRows: 1,
					widths: [65, '*', '*'],
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Descuento:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.descuento, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Retención I.S.R:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.retencion_isr, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		} else if (!comprobante.descuento || comprobante.descuento === '0.00') {
			return {
				table: {
					headerRows: 1,
					widths: [65, '*', '*'],
					body: [
						[
							'',
							{ text: 'Subtotal:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Retención I.V.A:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.retencion_iva, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'Retención I.S.R:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.retencion_isr, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
						[
							'',
							{ text: 'TOTAL:', style: 'tableHeader' },
							{
								text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
								style: 'totalPrice',
							},
						],
					],
				},
				layout: 'noBorders',
			};
		}

		return {
			table: {
				headerRows: 1,
				widths: layout.totales.widths,
				body: [
					[
						'',
						{ text: 'Subtotal:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.subtotal, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
					[
						'',
						{ text: 'Descuento:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.descuento, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
					[
						'',
						{ text: 'I.V.A:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.iva, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
					[
						'',
						{ text: 'Retención I.V.A:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.retencion_iva, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
					[
						'',
						{ text: 'Retención I.S.R:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.retencion_isr, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
					[
						'',
						{ text: 'TOTAL:', style: 'tableHeader' },
						{
							text: `${aMoneda(comprobante.total, opcionesPesosMexicanos)}`,
							style: 'totalPrice',
						},
					],
				],
			},
			layout: 'noBorders',
		};
	};
	const table = () => {
		console.log('TABLTE!!', comprobante);
		const quantityDecimals = layout.tableStyle?.quantityDecimals ?? 6;
		const isNumIdentificacion = comprobante.comprobantesDetalles.some(
			(comprobanteDetalle: any) => {
				if (comprobanteDetalle.numero_identificacion)
					return comprobanteDetalle.numero_identificacion.length > 0;
			}
		);

		console.log('NoIDENT:::', isNumIdentificacion);
		if (isPixcuaInvoice) {
			const body = comprobante.comprobantesDetalles.map((detalle: any, index: number) => {
				const fillColor = alternateRowFill(index);
				return [
					{
						stack: [
							{ text: detalle.concepto, bold: true, fontSize: 9, color: '#174C61' },
							{
								text: [detalle.concepto_sat_id, detalle.numero_identificacion].filter(Boolean).join(' - '),
								fontSize: 7,
								color: '#5D7B86',
								margin: [0, 3, 0, 0],
							},
							{ text: `${detalle.unidad_sat_id} - ${detalle.unidad}`, fontSize: 7, color: '#5D7B86' },
						],
						fillColor,
						margin: [6, 6, 6, 6],
					},
					{
						text: aMoneda(detalle.precio_unitario, opcionesPesosMexicanos),
						alignment: 'right', fontSize: 9, color: '#174C61', fillColor, margin: [4, 8, 4, 8],
					},
					{
						text: detalle.cantidad.toFixed(quantityDecimals),
						alignment: 'center', fontSize: 9, color: '#174C61', fillColor, margin: [4, 8, 4, 8],
					},
					{
						text: aMoneda(detalle.importe, opcionesPesosMexicanos),
						alignment: 'right', bold: true, fontSize: 9, color: '#174C61', fillColor, margin: [4, 8, 4, 8],
					},
				];
			});
			body.unshift([
				{ text: 'DESCRIPCIÓN', style: 'conceptTableHeader', margin: [6, 6, 6, 6] },
				{ text: 'PRECIO', style: 'conceptTableHeader', alignment: 'right', margin: [4, 6, 4, 6] },
				{ text: 'CANT.', style: 'conceptTableHeader', alignment: 'center', margin: [4, 6, 4, 6] },
				{ text: 'TOTAL', style: 'conceptTableHeader', alignment: 'right', margin: [4, 6, 4, 6] },
			]);
			return {
				table: { headerRows: 1, widths: ['*', 72, 54, 78], body },
				layout: {
					hLineColor: () => '#C7E3E8',
					vLineColor: () => '#C7E3E8',
					hLineWidth: (index: number) => index === 1 ? 1 : 0.25,
					vLineWidth: () => 0,
				},
			};
		}

		const detalleArray = comprobante.comprobantesDetalles.map((detalle: any, index: number) => {
			const fillColor = alternateRowFill(index);
			return [
				{
					text: `${detalle.cantidad.toFixed(quantityDecimals)}`,
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 8.5,
					fillColor,
				},
				{
					text: detalle.numero_identificacion
						? `${detalle.numero_identificacion}`
						: '',
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 8.5,
					fillColor,
				},
				{
					text: `${detalle.unidad_sat_id} - ${detalle.unidad}`,
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 10,
					fillColor,
				},
				{
					text: ` ${detalle.concepto_sat_id} - ${detalle.concepto}`,
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 8.5,
					fillColor,
				},
				{
					text: `${aMoneda(detalle.precio_unitario, opcionesPesosMexicanos)}`,
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 8.5,
					alignment: 'right',
					fillColor,
				},
				{
					text: `${aMoneda(detalle.importe, opcionesPesosMexicanos)}`,
					italics: true,
					color: layout.theme?.bodyTextColor ?? 'gray',
					fontSize: 8.5,
					alignment: 'right',
					fillColor,
				},
			];
		});
		detalleArray.unshift([
			{ text: cfg.labels.cantidad, style: 'conceptTableHeader' },
			{ text: cfg.labels.numeroIdentificacion, style: 'conceptTableHeader' },
			{ text: cfg.labels.unidad, style: 'conceptTableHeader' },
			{ text: cfg.labels.concepto, style: 'conceptTableHeader' },
			{ text: cfg.labels.precio, style: 'conceptTableHeader' },
			{ text: cfg.labels.importe, style: 'conceptTableHeader' },
		]);
		const detalleArrayWithoutNumIdentificacion =
			comprobante.comprobantesDetalles.map((detalle: any, index: number) => {
				const fillColor = alternateRowFill(index);
				return [
					{
						text: `${detalle.cantidad.toFixed(quantityDecimals)}`,
						italics: true,
						color: layout.theme?.bodyTextColor ?? 'black',
						bold: true,
						fontSize: 8.5,
						fillColor,
					},
					{
						text: `${detalle.unidad_sat_id} - ${detalle.unidad}`,
						italics: true,
						color: layout.theme?.bodyTextColor ?? 'black',
						fontSize: 10,
						fillColor,
					},
					{
						text: ` ${detalle.concepto_sat_id} - ${detalle.concepto}`,
						italics: true,
						color: layout.theme?.bodyTextColor ?? 'black',
						bold: true,
						fontSize: 8.5,
						fillColor,
					},
					{
						text: `${aMoneda(detalle.precio_unitario, opcionesPesosMexicanos)}`,
						italics: true,
						color: layout.theme?.bodyTextColor ?? 'black',
						bold: true,
						fontSize: 8.5,
						alignment: 'right',
						fillColor,
					},
					{
						text: `${aMoneda(detalle.importe, opcionesPesosMexicanos)}`,
						italics: true,
						color: layout.theme?.bodyTextColor ?? 'black',
						bold: true,
						fontSize: 8.5,
						alignment: 'right',
						fillColor,
					},
				];
			});
		detalleArrayWithoutNumIdentificacion.unshift([
			{ text: cfg.labels.cantidad, style: 'conceptTableHeader' },
			{ text: cfg.labels.unidad, style: 'conceptTableHeader' },
			{ text: cfg.labels.concepto, style: 'conceptTableHeader' },
			{ text: cfg.labels.precio, style: 'conceptTableHeader' },
			{ text: cfg.labels.importe, style: 'conceptTableHeader' },
		]);

		if (isNumIdentificacion) {
			return {
				table: {
					headerRows: 1,
					widths: cfg.table.widthsWithId as any,
					body: detalleArray,
				},
				layout: layout.theme?.borderColor ? {
					hLineColor: () => layout.theme?.borderColor,
					vLineColor: () => layout.theme?.borderColor,
					hLineWidth: () => layout.tableStyle?.borderWidth ?? 0.5,
					vLineWidth: () => layout.tableStyle?.borderWidth ?? 0.5,
				} : undefined,
			};
		} else {
			return {
				table: {
					headerRows: 1,
					widths: cfg.table.widthsWithoutId as any,
					body: detalleArrayWithoutNumIdentificacion,
				},
				layout: layout.theme?.borderColor ? {
					hLineColor: () => layout.theme?.borderColor,
					vLineColor: () => layout.theme?.borderColor,
					hLineWidth: () => layout.tableStyle?.borderWidth ?? 0.5,
					vLineWidth: () => layout.tableStyle?.borderWidth ?? 0.5,
				} : undefined,
			};
		}
	};
	const waterMark = () => {
		if (comprobante.estatus_comprobante_id === 1) {
			return {
				text: 'Cancelado',
				color: 'red',
				opacity: 0.3,
				bold: true,
				italics: false,
			};
		}
		return {
			text: '',
			color: 'red',
			opacity: 0,
			bold: true,
			italics: false,
		};
	};
	const imgLogo = (): any => {
		const headerTextColor = layout.header?.textColor ?? 'black';
		const headerLogo = promesa || (layout.header?.usePixcuaPlaceholderLogo ? brandPixcua : '');
		const dateBelowLogo = layout.header?.datePosition === 'belowLogo';
		if (isPixcuaInvoice) return { text: '' };
		if (usesColoredHeader) {
			return {
				table: {
					widths: ['*', 'auto'],
					body: [[
						{
							text: 'FACTURA',
							bold: true,
							fontSize: 13,
							color: layout.theme?.primaryColor,
						},
						{
							stack: [
								{ text: `Fecha: ${fechaComprobante}`, alignment: 'right', color: headerTextColor },
								{ text: `Folio: ${comprobante.serie}${comprobante.folio}`, alignment: 'right', color: headerTextColor, style: 'folio', margin: [0, 4, 0, 0] },
							],
						},
					]],
				},
				layout: 'noBorders',
				margin: [0, 0, 0, 12],
			};
		}
		if (headerLogo || dateBelowLogo) {
			const logoWidth = layout.header?.logoWidth ?? 80;
			const logoHeight = layout.header?.logoHeight;
			const dateBlock = {
				fillColor: layout.header?.dateBackgroundColor,
				margin: layout.header?.dateBlockMargin ?? [0, 0, 0, 0],
				stack: [
					...(layout.header?.title
						? [{
							alignment: 'right',
							text: layout.header.title,
							color: layout.header.titleColor ?? headerTextColor,
							fontSize: layout.header.titleFontSize ?? 18,
							bold: true,
							margin: [0, 0, 0, 8],
						}]
						: []),
					{
						alignment: 'right',
						text: `Fecha: ${fechaComprobante} \n`,
						color: headerTextColor,
					},
					{
						alignment: 'right',
						style: 'folio',
						text: `Folio: ${comprobante.serie}${comprobante.folio}`,
						color: headerTextColor,
						margin: [0, 5, 0, 0],
					},
				],
			};
			const logo = headerLogo
				? {
					image: `${headerLogo}`,
					...(logoHeight
						? { fit: [logoWidth, logoHeight] }
						: { width: logoWidth }),
				}
				: {
					text: '',
					margin: [0, 0, 0, logoHeight ?? 70],
				};

			if (dateBelowLogo) {
				return {
					stack: [
						logo,
						{
							table: {
								widths: [layout.header?.dateWidth ?? 230],
								body: [[dateBlock]],
							},
							layout: 'noBorders',
							margin: [0, 8, 0, 0],
						},
					],
				};
			}

			return {
				table: {
					widths: ['*', layout.header?.dateWidth ?? '*'],
					body: [
						[
							logo,
							dateBlock,
						],
					],
				},
				layout: 'noBorders',
			};
		}

		return {
			table: {
				widths: ['*', '*'],
				body: [
					[
						{ text: '', style: 'tableHeader' },
						[
							{
								alignment: 'right',
								text: `Fecha: ${fechaComprobante} \n`,
								margin: margenes(comprobante.empresa_id,'header'),
							},
							{
								alignment: 'right',
								style: 'folio',
								text: `Folio: ${comprobante.serie}${comprobante.folio}`,
								margin: [0, 5, 0, 0],
							},
						],
					],
				],
			},
			layout: 'noBorders',
		};
	};
	const coloredHeader = (): any => {
		if (isPixcuaInvoice) {
			const headerHeight = layout.header?.height ?? 106;
			const headerWidth = 595;
			const logoWidth = layout.header?.logoWidth ?? 145;
			const headerLogo = promesa || brandPixcua;
			const gradient = `
				<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${headerWidth}" height="${headerHeight}" viewBox="0 0 ${headerWidth} ${headerHeight}">
					<defs>
						<linearGradient id="pixcuaHeaderGradient" x1="0" y1="0" x2="1" y2="0">
							<stop offset="0%" stop-color="#FFFFFF" />
							<stop offset="30%" stop-color="#FFFFFF" />
							<stop offset="65%" stop-color="#00ABC2" />
							<stop offset="100%" stop-color="#174C61" />
						</linearGradient>
					</defs>
					<rect width="${headerWidth}" height="${headerHeight}" fill="url(#pixcuaHeaderGradient)" />
					<image x="40" y="0" width="${logoWidth}" height="${headerHeight}" preserveAspectRatio="xMinYMid meet" xlink:href="${headerLogo}" />
				</svg>`;
			return {
				stack: [
					{ svg: gradient, width: headerWidth, height: headerHeight },
					{
						table: {
							widths: [205, '*', 174],
							heights: [headerHeight],
							body: [[
								{ text: '' },
								{
									text: 'FACTURA',
									bold: true,
									fontSize: 26,
									color: '#FFFFFF',
									alignment: 'center',
									margin: [0, 32, 0, 0],
								},
								{
									stack: [
										{ text: 'COMPROBANTE', color: '#BCECF2', fontSize: 7, bold: true, alignment: 'right' },
										{ text: `${comprobante.serie}${comprobante.folio}`, color: '#FFFFFF', fontSize: 18, bold: true, alignment: 'right', margin: [0, 4, 0, 8] },
										{ text: fechaComprobante, color: '#FFFFFF', fontSize: 8, alignment: 'right' },
									],
									margin: [12, 20, 0, 12],
								},
							]],
						},
						layout: 'noBorders',
						margin: [0, -headerHeight, 40, 0],
					},
				],
			};
		}
		if (layout.header?.issuerBesideBackgroundImage) {
			const issuerPosition = layout.header.issuerPosition ?? {
				x: 300,
				y: 55,
				width: 250,
			};
			return {
				table: {
					widths: [issuerPosition.width],
					body: [[{
						stack: getColumnBasedOnEmpresa(
							comprobante.empresa_id,
							empresaInfo,
							regimenEmpresa,
							isDocRelacionado,
							'right'
						),
					}]],
				},
				layout: 'noBorders',
				absolutePosition: {
					x: issuerPosition.x,
					y: issuerPosition.y,
				},
			};
		}
		if (!usesColoredHeader) return undefined;
		const headerMunicipality = empresaInfo.municipio || empresaInfo.localidad;
		const headerAddressFirstLine = [
			[empresaInfo.calle, empresaInfo.no_exterior].filter(Boolean).join(' '),
			empresaInfo.colonia,
			headerMunicipality,
		].filter(Boolean).join(', ');
		const headerAddressSecondLine = [
			empresaInfo.localidad && empresaInfo.localidad !== headerMunicipality
				? empresaInfo.localidad
				: '',
			empresaInfo.estado,
			empresaInfo.pais,
			empresaInfo.codigo_postal ? `C.P. ${empresaInfo.codigo_postal}` : '',
		].filter(Boolean).join(', ');
		const headerAddress = [headerAddressFirstLine, headerAddressSecondLine]
			.filter(Boolean)
			.join('\n');
		const headerTextColor = layout.header?.textColor ?? '#FFFFFF';
		const contactRows = [
			{ icon: faPhone, label: 'TELÉFONO', value: empresaInfo.telefono || '' },
			{ icon: faEnvelope, label: 'EMAIL', value: empresaInfo.email || '' },
			{ icon: faLocationDot, label: 'DIRECCIÓN', value: headerAddress.toUpperCase() },
		].filter(({ value }) => Boolean(value));
		const logoWidth = layout.header?.logoWidth ?? 80;
		const logoHeight = layout.header?.logoHeight ?? 55;
		const logoOrName = promesa
			? { image: `${promesa}`, fit: [logoWidth, logoHeight] }
			: { text: empresaInfo.nombre, bold: true, fontSize: 15, color: headerTextColor };

		return {
			table: {
				widths: ['*'],
				heights: [layout.header?.height ?? 90],
				body: [[{
					fillColor: layout.header?.backgroundColor ?? '#91AEB5',
					margin: [35, 14, 35, 10],
					columns: [
						logoOrName,
						{
							stack: contactRows.map(({ icon, label, value }) => ({
								columns: [
									{ text: '', width: '*' },
									{
										width: 'auto',
										alignment: 'right',
										stack: [
											{ text: label, color: headerTextColor, bold: true, fontSize: 6 },
											{ text: value, color: headerTextColor, fontSize: 7 },
										],
									},
									{
										svg: fontAwesomeSvg(icon, headerTextColor),
										width: layout.header?.contactIconSize ?? 12,
										margin: [0, 2, 0, 0],
									},
								],
								columnGap: 5,
								margin: [0, 0, 0, 4],
							})),
						},
					],
				}]],
			},
			layout: 'noBorders',
			margin: [0, 0, 0, 0],
		};
	};
	const imgTemplete = () => {
			console.log(promesaTemplete);
			
			if (layout.backgroundImage !== false && promesaTemplete) {
				return {
					table: {
						widths: ['*', '*'],
						body: [
							[
								{
									image: `${promesaTemplete}`,
									width: layout.background?.width ?? 620,
									height: layout.background?.height ?? 842,
									absolutePosition: {
										x: layout.background?.x ?? 0,
										y: layout.background?.y ?? 0,
									},
								},
							],
						],
					},
					layout: 'noBorders',
				};
		}
		return {
			table: {
				widths: ['*', '*'],
				body: [
					[
						{ text: '', style: 'tableHeader' },
						
					],
				],
			},
			layout: 'noBorders',
		};
	};
	const isDocRelacionado = () => {
		if (docRelacionados.comprobante_relacionado_id) {
			return `\n Documento relacionado:${docRelacionados.tipoRelacion.descripcion} ${docRelacionados.tipoRelacionesComprobante.uuid}`;
		}
		return ``;
	};
	function getColumnBasedOnEmpresa(
		empresaId: number,
		empresaInfo: any,
		regimenEmpresa: any,
		isDocRelacionado: () => string,
		alignment: 'left' | 'right' = 'left'
	) {
	
		// Retornar el arreglo con la alineación dinámica
		const columns: any[] = [
			{
				alignment: alignment,
				bold: true,
				text: `${empresaInfo.nombre} \n`,
				fontSize: 10,
			},
			{
				alignment: alignment,
				text: `R.F.C. ${empresaInfo.rfc}\n`,
				fontSize: 10,
			},
			{
				alignment: alignment,
				text: `Régimen fiscal: ${empresaInfo.regimen_fiscal} - ${regimenEmpresa.descripcion}. \n`,
				fontSize: 10,
			},
			{
				alignment: alignment,
				text: `${empresaInfo.calle}, ${empresaInfo.no_exterior}, ${empresaInfo.no_interior} ${empresaInfo.colonia} ${empresaInfo.codigo_postal}. \n`,
				fontSize: 10,
			},
			{
				alignment: alignment,
				text: `${empresaInfo.localidad}, ${empresaInfo.estado}, ${empresaInfo.pais}. \n`,
				fontSize: 10,
			},
			{
				alignment: alignment,
				text: `${isDocRelacionado()}`,
				fontSize: 10,
			},
		];
	
		return columns;
	}
	const partyInformation = () => {
		if (!isPixcuaInvoice) {
			return {
				alignment: 'justify',
				margin: margenes(comprobante.empresa_id,'datosEmpresa'),
				columns: layout.header?.issuerBesideBackgroundImage
					? [getDynamicAlignmentColumn(comprobante.empresa_id, false), { text: '' }]
					: [
						getColumnBasedOnEmpresa(comprobante.empresa_id, empresaInfo, regimenEmpresa, isDocRelacionado),
						getDynamicAlignmentColumn(comprobante.empresa_id, true),
					],
			};
		}

		return {
			table: {
				widths: ['*', 230],
				body: [[
					{
						stack: [
							{ text: 'FACTURAR A', color: '#00ABC2', bold: true, fontSize: 8, margin: [0, 0, 0, 6] },
							...getDynamicAlignmentColumn(comprobante.empresa_id, false),
						],
						margin: [0, 0, 18, 0],
					},
					{
						stack: [
							{ text: 'EMISOR', color: '#00ABC2', bold: true, fontSize: 8, margin: [0, 0, 0, 6] },
							...getColumnBasedOnEmpresa(comprobante.empresa_id, empresaInfo, regimenEmpresa, isDocRelacionado),
						],
						fillColor: '#EEF8FA',
						margin: [12, 10, 12, 10],
					},
				]],
			},
			layout: 'noBorders',
			margin: margenes(comprobante.empresa_id,'datosEmpresa'),
		};
	};

	const totalsAndPayment = () => {
		if (!isPixcuaInvoice) {
			return {
				alignment: 'justify',
				margin: [0, 0, 0, 0],
				columns: [[{ alignment: 'left', bold: true, text: ` \n` }], [totales()]],
			};
		}
		return {
			columns: [
				{
					width: '*',
					stack: [
						{ text: 'DATOS DE PAGO', color: '#00ABC2', bold: true, fontSize: 8, margin: [0, 4, 0, 5] },
						{ text: `Forma de pago: ${comprobante.forma_de_pago}`, fontSize: 8, color: '#214D5C' },
						{ text: `Método de pago: ${comprobante.metodo_de_pago}`, fontSize: 8, color: '#214D5C' },
						{ text: `(${comprobante.cantidad_letra})`, fontSize: 7, color: '#214D5C', margin: [0, 5, 12, 0] },
					],
				},
				{ width: 170, stack: [totales()] },
			],
			columnGap: 18,
			margin: [0, 4, 0, 8],
		};
	};
function getDynamicAlignmentColumn(empresaId: number, isRightAlignment: boolean) {
    // Columna derecha: datos del Receptor (cliente del comprobante)
    const alignment = empresaId === 5 || empresaId === 18 || empresaId === 63 ? 'left' : isRightAlignment ? 'right' : 'left';
    const receptorLine1 = `${comprobante.nombre || ''}`;
    const receptorRfc = `R.F.C. ${comprobante.rfc || ''}`;
    const regimenDesc = regimenCliente?.descripcion || '';
    const regimenReceptor = `Régimen Fiscal: ${comprobante.c_regimen_fiscal_Cliente || ''} - ${regimenDesc}.`;
    const direccionReceptor = `${comprobante.calle ? comprobante.calle : ''}${
        comprobante.no_exterior ? ', ' + comprobante.no_exterior : ''
      }${comprobante.no_interior ? ', ' + comprobante.no_interior : ''}${
        comprobante.colonia ? ', ' + comprobante.colonia : ''
      }${comprobante.codigo_postal ? ', ' + comprobante.codigo_postal : ''} \n ${
        comprobante.localidad ? comprobante.localidad : ''
      }${comprobante.estado ? ', ' + comprobante.estado : ''}${comprobante.pais ? ', ' + comprobante.pais : ''}`;

    const columns: any[] = [
      {
        alignment,
        bold: true,
        text: receptorLine1,
        fontSize: 10,
      },
      {
        alignment,
        text: receptorRfc,
        fontSize: 10,
      },
      {
        alignment,
        text: regimenReceptor,
        fontSize: 10,
      },
      {
        alignment,
        text: direccionReceptor,
        fontSize: 10,
      },
      {
        alignment,
        text: `${isDocRelacionado()}`,
        fontSize: 10,
      },
    ];
    return columns;
  }
	
	
  const margenes=(empresa_id:number, ubicacion:string): [number, number, number, number] =>{
      const lc = getLayoutConfig(empresa_id);
      const m = (lc.margins as any)[ubicacion];
      if (Array.isArray(m) && m.length === 4) return m as [number, number, number, number];
      return [0,0,0,0];
    }


  // Ajuste dinámico del espacio reservado según el offset superior (pageMargins[1])
  const spacerHeight = Math.max(
    0,
    ((layout.contentSpacerHeight ?? 360) as number) - ((layout.pageMargins?.[1] ?? 0) as number)
  );

  let docDefinition: TDocumentDefinitions = {
    // Permitir configurar desde dónde comienzan los contenidos del PDF (offset global)
    pageMargins: Array.isArray(layout.pageMargins) && layout.pageMargins.length === 4
      ? (layout.pageMargins as [number, number, number, number])
      : [0, 0, 0, 0],
    info: {
      title: `${comprobante.uuid}.pdf`,
    },
		watermark: waterMark(),
		header: coloredHeader(),
		footer: {
			image: `${brandPixcua}`,
			width: layout.footer?.logoWidth ?? 50,
			alignment: 'center',
			margin: layout.footer?.margin ?? [0, 8, 0, 0],
			...(layout.footer?.opacity !== undefined
				? { opacity: layout.footer.opacity }
				: {}),
		},
		background: [
			imgTemplete(),
		  ],
		content: [
			imgLogo(),
			
			partyInformation(),
			{
				style: 'tableExample',
				table: {
					widths: ['*'],
					heights: [spacerHeight, 'auto'],
					body: [
						[
							{
								columns: [
									[
										table(),
										totalsAndPayment(),
									],
								],
							},
						],
						[
							{
								margin: margenes(comprobante.empresa_id,'footer'),
								columns: [
									[
										...(isPixcuaInvoice ? [] : [
											{
												alignment: 'left',
												fontSize: cfg.styles.footerLabelFontSize,
												text: `Cantidad en letra:`,
												bold: true,
											},
											{
												alignment: 'left',
												fontSize: cfg.styles.footerAmountFontSize,
												text: `(${comprobante.cantidad_letra})`,
											},
											{
												alignment: 'left',
												fontSize: cfg.styles.footerTextFontSize,
												margin: [0, 8, 0, 0],
												text: `Forma de pago: ${comprobante.forma_de_pago}. Método de pago: ${comprobante.metodo_de_pago}. No. de cuenta: ${comprobante.num_cta_pago}.`,
											},
										]),
            {
              alignment: 'left',
              fontSize: cfg.styles.footerTextFontSize,
              text: `Lugar de expedición: ${comprobante.lugar_expedicion}. Uso de CFDI: ${comprobante.tipo_uso}.  Tipo de comprobante: ${comprobante.tipo_de_comprobante}.`,
            },
										{
											alignment: 'left',
											fontSize: 8,
											text: `Regimen: ${comprobante.c_regimen_fiscal_Cliente}.`,
										},
										{
											alignment: 'left',
											fontSize: 9,
											bold: true,
											text: `Este documento es una representación impresa de un CFDI.`,
										},
										timbre(),
									],
								],
							},
						],
					],
				},
				layout: 'noBorders',
			},
		],
		styles: {
			header: {
				fontSize: 18,
				bold: true,
			},
			bigger: {
				fontSize: 15,
				italics: true,
			},
			folio: {
				background: layout.header?.folioBackgroundColor ?? '#ffffff',
				color: layout.header?.textColor ?? 'black',
				decoration: 'underline',
				fontSize: cfg.styles.folioFontSize,
				bold: true,
			},
			cadena: {
				background: '#000000',
				color: '#ffffff',
				fontSize: 16,
				bold: true,
				noWrap: true,
			},
			tableExample: {
				margin: [0, 5, 0, 15],
				fontSize: cfg.styles.rowMediumFontSize,
			},
			tableHeader: {
				bold: true,
				fontSize: cfg.styles.tableHeaderFontSize,
				color: 'black',
			},
			conceptTableHeader: {
				bold: true,
				fontSize: cfg.styles.tableHeaderFontSize,
				color: layout.theme?.tableHeaderTextColor ?? 'black',
				fillColor: layout.theme?.tableHeaderBackgroundColor,
			},
			totalPrice: {
				fontSize: cfg.styles.rowMediumFontSize,
				color: layout.theme?.totalPriceColor ?? layout.theme?.primaryColor ?? 'black',
				alignment: 'right',
			},
			footerStyle: {
				background: '#000000',
				color: '#ffffff',
				fontSize: 16,
				bold: true,
			},
		},
		defaultStyle: {
			columnGap: 20,
		},
	};
	if (tipo === 'abrir') {
		return pdfMake.createPdf(docDefinition).open();
	} else if (tipo === 'descargar') {
		return new Promise<void>((resolve) => {
			pdfMake.createPdf(docDefinition).download(`${comprobante.uuid}`, () => {
				// FileSaver programa el clic de descarga para el siguiente ciclo. Esperar
				// otro ciclo evita habilitar controles antes de mostrar el diálogo nativo.
				window.setTimeout(resolve, 0);
			});
		});
	} else if (tipo === 'blob') {
		return new Promise<void>((resolve, reject) => {
			pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
				Promise.resolve(onPdfBlobReady?.(blob)).then(resolve).catch(reject);
			});
		});
	}
};

interface iPdfProps {
    uuid: any;
    tipo: any;
    isMyDataValid?: any;
    id?: any;
	autoOpen?: boolean;
	onAutoActionComplete?: (success: boolean) => void;
	onPdfBlobReady?: (blob: Blob) => Promise<void> | void;
}
//Comienza FunctionalComponent
const PdfMakeOpenTest = ({
	uuid,
	tipo,
	isMyDataValid,
	id,
	autoOpen,
	onAutoActionComplete,
	onPdfBlobReady,
}: iPdfProps) => {
	const initComprobante: iComprobante = {
		c_regimen_fiscal_Cliente: '',
		c_regimen_fiscal_Empresa: '',
		calle: '',
		cantidad_letra: '',
		certificado: null,
		cliente_id: 0,
		codigo_postal: '0',
		codigo_qr: '',
		colonia: '',
		comprobantesDetalles: [],
		descuento: 0,
		empresa_id: 0,
		estado: '',
		estatus_comprobante_id: 0,
		fecha: '',
		fecha_timbrado: null,
		folio: 0,
		forma_de_pago: '',
		id: 0,
		isr: null,
		iva: 0,
		localidad: '',
		lugar_expedicion: '',
		metodo_de_pago: '',
		moneda: '',
		motivo_de_descuento: '',
		municipio: '',
		no_certificado: '',
		no_certificado_sat: null,
		no_exterior: '',
		no_interior: '',
		nombre: '',
		num_cta_pago: '',
		pagado: false,
		pais: '',
		referencia: '',
		retencion_isr: 0,
		retencion_iva: 0,
		rfc: '',
		sello: null,
		sello_sat: null,
		serie: '',
		subtotal: 0,
		subtotal_iva_cero: 0,
		tasa: 0,
		timbrado: false,
		tipo_cambio: null,
		tipo_de_comprobante: '',
		tipo_uso: '',
		total: 0,
		uuid: '',
		version: '',
		comprobante_relacionado_uuid: null,
		tipo_relacion_id: null,
	};
	const [comprobante, setComprobante] = useState(initComprobante);
	const [empresaInfo, setEmpresaInfo] = useState<iMiEmpresaInfoToPDF>(
		initMiEmpresaFormulario
	);
	const [regimenCliente, setRegimenCliente] = useState({
		c_RegimenFiscal: 0,
		descripcion: '',
		fisica: false,
		id: 0,
		moral: false,
	});
	const [regimenEmpresa, setRegimenEmpresa] = useState({
		c_RegimenFiscal: 0,
		descripcion: '',
		fisica: false,
		id: 0,
		moral: false,
	});
	const [docRelacionados, setDocRelacionados] = useState({});
	const [openRequested, setOpenRequested] = useState(false);
	const [isOpening, setIsOpening] = useState(false);
	const autoRanRef = useRef(false);

	//Obtiene la Info del comprobante Byuuid
	useEffect(() => {
		const comprobanteData = getComprobantesById(uuid);
		comprobanteData
			.then((respuesta) => {
				if (respuesta.status === 200) {
					setComprobante(respuesta.data);
				}
			})
			.catch((error) => {
				if (error.response.status === 500) {
					console.log('error500');
					console.log(error.response.data.message);
				} else if (error.response.status === 400) {
					console.log('error400');
					console.log(error.response.data.message);
				} else if (error.response.status === 401) {
					console.log('error401');
					console.log(error.response.data);
				} else if (error.response.status === 403) {
					console.log('error403');
					console.log(error.response.data.message);
				} else if (error.response.status === 404) {
					console.log('error404');
					console.log(error.response.data.message);
				} else {
					console.log(error.response.data.message);
				}
			});
    // No consultes documentos relacionados hasta conocer si el comprobante tiene relación

		// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

//Obtiene MiEmpresa Info by id
	useEffect(() => {
		if (comprobante.empresa_id) {
			const dataEmpresa = getEmpresaInfoById(comprobante.empresa_id);
			dataEmpresa
				.then((respuesta) => {
					if (respuesta.status === 200) {
						setEmpresaInfo({
							...empresaInfo,
							nombre: respuesta.data.nombre,
							rfc: respuesta.data.rfc,
							codigo_postal: respuesta.data.codigo_postal,
							calle: respuesta.data.calle ? respuesta.data.calle : '',
							no_exterior: respuesta.data.no_exterior
								? respuesta.data.no_exterior
								: '',
							no_interior: respuesta.data.no_interior
								? respuesta.data.no_interior
								: '',
							colonia: respuesta.data.colonia ? respuesta.data.colonia : '',
							localidad: respuesta.data.localidad ? respuesta.data.localidad : '',
							municipio: respuesta.data.municipio ? respuesta.data.municipio : '',
							estado: respuesta.data.estado ? respuesta.data.estado : '',
							pais: respuesta.data.pais ? respuesta.data.pais : '',
							regimen_fiscal: respuesta.data.regimen_fiscal
								? respuesta.data.regimen_fiscal
								: '',
							telefono: respuesta.data.telefono ? respuesta.data.telefono : '',
							email: respuesta.data.email ? respuesta.data.email : '',
							logo: respuesta.data.logo ? respuesta.data.logo : '',
							imagen_templete: respuesta.data.imagen_templete ? respuesta.data.imagen_templete : '',
						});
					}
				})
				.catch((error) => {
					if (error.response.status === 500) {
						console.log('error500');
						console.log(error.response.data.message);
					} else if (error.response.status === 400) {
						console.log('error400');
						console.log(error.response.data.message);
					} else if (error.response.status === 403) {
						console.log('error403');
						console.log(error.response.data.message);
					} else if (error.response.status === 404) {
						console.log('error404');
						console.log(error.response.data.message);
					} else {
						console.log(error.response.data.message);
					}
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comprobante.empresa_id]);

 // Cargar documentos relacionados sólo si el comprobante tiene relación definida
 useEffect(() => {
   if (comprobante?.id && (comprobante?.tipo_relacion_id || comprobante?.comprobante_relacionado_uuid)) {
     const req = getDocRelacionadoByComprobanteId(comprobante.id);
     req
       .then((respuesta) => {
         if (respuesta.status === 200) {
           setDocRelacionados(respuesta.data);
         }
       })
       .catch((error) => {
         if (error?.response?.status === 404) {
           // Sin relación: no es error funcional, omitimos
           return;
         }
         console.log('Error obteniendo documento relacionado', error?.response?.data || error);
       });
   }
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [comprobante.id, comprobante.tipo_relacion_id, comprobante.comprobante_relacionado_uuid]);

	// Obtiene y asigna Régimenes Cliente y Miempresa
	useEffect(() => {
		//Get Regimenes
		if (comprobante.c_regimen_fiscal_Cliente && empresaInfo.regimen_fiscal) {
			const data = getRegimenes();
			data
				.then((respuesta) => {
					// console.log(respuesta);
					if (respuesta.status === 201) {
						// setRegimenes(nuevaDataRegimenes);
						const regimenesArray = respuesta.data;

						setRegimenCliente(
							regimenesArray.find(
								(regimen: any) =>
									regimen.c_RegimenFiscal ===
									Number(comprobante.c_regimen_fiscal_Cliente)
							)
						);
						setRegimenEmpresa(
							regimenesArray.find(
								(regimen: any) =>
									regimen.c_RegimenFiscal === Number(empresaInfo.regimen_fiscal)
							)
						);
					}
				})
				.catch((error) => {
					if (error.response.status === 500) {
						console.log('error500');
						console.log(error.response.data.message);
					} else if (error.response.status === 400) {
						console.log('error400');
						console.log(error.response.data.message);
					} else if (error.response.status === 403) {
						console.log('error403');
						console.log(error.response.data.message);
					} else if (error.response.status === 404) {
						console.log('error404');
						console.log(error.response.data.message);
					} else {
						console.log(error.response.data.message);
					}
				});
		}
	}, [comprobante, empresaInfo]);
const image_templete_promise = async (): Promise<string> => {
	const companyLayout = await loadPDFLayout(comprobante?.empresa_id);

	if (companyLayout.backgroundUrl) {
		try {
			return await getBase64ImageFromURL(companyLayout.backgroundUrl);
		} catch {}
	}

	return '';
};
	const abirPDF = async () => {
		if (!empresaInfo.nombre || !comprobante.uuid) {
			setOpenRequested(true);
			return;
		}

		if (isOpening) return;

		setOpenRequested(false);
		setIsOpening(true);
		let promesa:any='';
		try {
			if(empresaInfo.logo!==''){
				promesa = await getBase64ImageFromURL(
					`${baseURL}/empresas/imagen/${empresaInfo.logo}`
				);
			}
		} catch (error) {
			promesa = '';
		}
    let promesaTemplete: string = await image_templete_promise();
    // No mostrar alertas durante la construcción del PDF

        // Ruta pública (autoOpen): no exigir validaciones interactivas ni regimenes
        if (tipo === 'abrir' && autoOpen) {
            if (empresaInfo.nombre && comprobante.uuid) {
                openPDFTemplate({
                    comprobante,
                    empresaInfo,
                    regimenCliente,
                    regimenEmpresa,
                    tipo,
                    promesa,
                    promesaTemplete,
                    docRelacionados,
                });
				setIsOpening(false);
                return;
            }
        }

		if (isMyDataValid && isMyDataValid()) {
			if (empresaInfo.nombre && comprobante.uuid) {
				openPDFTemplate({
					comprobante,
					empresaInfo,
					regimenCliente,
					regimenEmpresa,
					tipo,
					promesa,
					promesaTemplete,
					docRelacionados,
				});
			}
		} else if (!(tipo === 'abrir' && autoOpen)) {
			Toast.fire({
				icon: 'warning',
				title: `Por favor, antes de continuar. Ingresa la información obligatoria de tu empresa en la sección "Configuracion / Mi cuenta"`,
				timer: 5000,
			});
		}
		setIsOpening(false);
	};
	const descargarPDF = async () => {
		let promesa;
		try {
			if (empresaInfo.logo!=='') {
				promesa = await getBase64ImageFromURL(
					`${baseURL}/empresas/imagen/${empresaInfo.logo}`
				);
			}
		} catch (error) {
			promesa = '';
		}
    let promesaTemplete:string = await image_templete_promise();
		
		if (
			empresaInfo.nombre &&
			comprobante.uuid
		) {
			await openPDFTemplate({
				comprobante,
				empresaInfo,
				regimenCliente,
				regimenEmpresa,
				tipo,
				promesa,
				promesaTemplete,
				docRelacionados,
				onPdfBlobReady,
			});
		}
	};
	// Efecto local: descarga al montar cuando se usa dentro del app (no público)
	useEffect(() => {
		if (tipo === 'descargar' && !autoOpen) {
			if (
				empresaInfo.nombre &&
				comprobante.uuid
			) {
				descargarPDF();
			}
		}
	}, [comprobante, empresaInfo, regimenCliente, regimenEmpresa, tipo, autoOpen]);

	// Si el usuario hace clic mientras el menú aún carga sus datos, conserva la
	// intención y abre el PDF tan pronto como la información mínima esté lista.
	useEffect(() => {
		if (openRequested && empresaInfo.nombre && comprobante.uuid) {
			abirPDF();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openRequested, empresaInfo.nombre, comprobante.uuid]);

    // Auto-abrir PDF (vista) o auto-descargar cuando se usa como ruta pública
    useEffect(() => {
        if (autoRanRef.current) return;
        if (autoOpen && empresaInfo?.nombre && comprobante?.uuid) {
            if (tipo === 'abrir') {
                autoRanRef.current = true;
                abirPDF();
			} else if (tipo === 'descargar' || tipo === 'blob') {
                autoRanRef.current = true;
				descargarPDF()
					.then(() => onAutoActionComplete?.(true))
					.catch(() => onAutoActionComplete?.(false));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tipo, autoOpen, empresaInfo, comprobante]);

    if (tipo === 'abrir') {
        // En modo autoOpen (ruta pública) no renderizamos botón
        if (autoOpen) return null;
        return (
            <MenuItem onClick={abirPDF} disabled={openRequested || isOpening}>
                {openRequested || isOpening ? 'Preparando documento...' : 'Ver PDF'}
            </MenuItem>
        );
    } else if (tipo === 'descargar') {
        // En modo autoOpen ocultamos el trigger visual
        if (autoOpen) return null;
        return (
            <MDTypography
                variant='button'
                color='text'
                onClick={descargarPDF}
                style={{ cursor: 'pointer', fontSize: '20px' }}
            >
                Descargar pdf
            </MDTypography>
        );
    }

	return null;
};

export default PdfMakeOpenTest;
